<template>
  <h4 v-if="title != null && title.length > 0" class="tw-mb-2">
    {{ title }}
  </h4>

  <div class="tw-justify-between tw-gap-4 md:tw-flex">
    <div class="tw-w-full md:tw-w-1/2">
      <GpAvatar
        v-if="!selectedFile"
        :src="avatarUrl"
        :alt="avatarId"
        :rounded="rounded"
        custom-size
        style="--gp-avatar-size: 100%"
      />
      <template v-if="selectedFile">
        <template v-if="mimeType !== 'image/gif'">
          <cropper-canvas
            ref="cropperCanvasEl"
            background
            class="tw-mt-4"
            style="max-height: 400px"
          >
            <cropper-image
              ref="cropperImageEl"
              :src="selectedFile"
              alt="Upload Image"
              rotatable
              scalable
              skewable
              translatable
            ></cropper-image>
            <cropper-shade hidden></cropper-shade>
            <cropper-handle action="select" plain></cropper-handle>
            <cropper-selection
              ref="cropperSelectionEl"
              initial-coverage="0.5"
              movable
              resizable
              :aspect-ratio="1"
            >
              <cropper-grid role="grid" bordered covered></cropper-grid>
              <cropper-crosshair centered></cropper-crosshair>
              <cropper-handle
                action="move"
                theme-color="rgba(255, 255, 255, 0.35)"
              ></cropper-handle>
              <cropper-handle action="n-resize"></cropper-handle>
              <cropper-handle action="e-resize"></cropper-handle>
              <cropper-handle action="s-resize"></cropper-handle>
              <cropper-handle action="w-resize"></cropper-handle>
              <cropper-handle action="ne-resize"></cropper-handle>
              <cropper-handle action="nw-resize"></cropper-handle>
              <cropper-handle action="se-resize"></cropper-handle>
              <cropper-handle action="sw-resize"></cropper-handle>
            </cropper-selection>
          </cropper-canvas>
        </template>
        <template v-else>
          <img class="tw-mt-4" :src="selectedFileString" alt="Upload Image" />
          <p class="tw-mt-2">
            Cropping GIFs is not supported. You can upload the GIF without further modification.
          </p>
        </template>
      </template>
    </div>
    <div class="tw-mt-4 tw-w-full md:tw-mt-0 md:tw-w-1/2">
      <p v-if="!selectedFile">Please select a local image to upload.</p>
      <div class="tw-mt-4 tw-flex tw-gap-4">
        <GpBtn
          v-if="selectedFile"
          :icon="['fas', 'fa-undo']"
          btn-type="warning"
          outline
          size="sm"
          label="Reset"
          @click="reset"
        />
        <OpenFileManagerBtn
          ref="fileInput"
          accept="image/*"
          upload-input-id="avatar-upload"
          @change="onFileSelect"
        />
      </div>
    </div>
  </div>
  <GpBtn
    v-if="!hideSaveButton"
    class="tw-mt-8 tw-w-full"
    :label="saveAvatarLabel"
    :disabled="!selectedFile"
    @click="saveImage()"
  />
</template>

<script lang="ts">
import axios from 'axios';
import { type CropperCanvas, type CropperImage, type CropperSelection } from 'cropperjs';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { computed, defineComponent, ref } from 'vue';
import { useAvatarStore } from 'gpcore-apikit/store/pinia/avatar';
import { GpAvatar, GpBtn } from '@gportal/uikit';
import useGlobalConfig from '@panel/composables/useGlobalConfig';
import OpenFileManagerBtn from 'gpcore-uikit/components/new/upload/OpenFileManagerBtn.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

export default defineComponent({
  props: {
    avatarId: {
      type: String,
      required: true,
    },
    avatarUrl: {
      type: String,
      required: true,
    },
    cropperCanvasSize: {
      type: Number,
      default: 512,
    },
    excludeGif: {
      type: Boolean,
      default: false,
    },
    hideSaveButton: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: true,
    },

    title: {
      type: String,
      default: '',
    },
    saveAvatarLabel: {
      type: String,
      default: 'Save Avatar',
    },
  },
  components: {
    GpBtn,
    GpAvatar,
    OpenFileManagerBtn,
  },
  emits: ['avatar-updated'],
  computed: {},

  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const token = computed(() => sessionStore.token);
    const currentAvatar = computed(() => sessionStore.session.user.avatarUrl);

    const file = ref<File>();
    const mimeType = ref<string>();
    const selectedFile = ref<string | null>();

    const selectedFileString = computed(() => {
      if (selectedFile.value == null) return '';
      return selectedFile.value;
    });

    const fileInput = ref<InstanceType<typeof OpenFileManagerBtn>>();
    const { addNotification } = useNotifications();

    // Refs for cropperjs v2 web components
    const cropperCanvasEl = ref<CropperCanvas>();
    const cropperImageEl = ref<CropperImage>();
    const cropperSelectionEl = ref<CropperSelection>();

    const croppedImage = ref<string>();
    const onFileSelect = (eventFile: File) => {
      croppedImage.value = '';
      file.value = eventFile;
      mimeType.value = eventFile.type;

      if (typeof FileReader === 'function') {
        const reader = new FileReader();
        reader.onload = async (event: ProgressEvent<FileReader>) => {
          selectedFile.value = event.target.result as string;
        };
        reader.readAsDataURL(file.value);
      } else {
        addNotification({
          message: 'Uploading files are not supported by your browser.',
          type: 'danger',
        });
      }
    };

    const config = useGlobalConfig();
    const avatarUploadURL = computed(() => `https://${config.gateway.host}/avatar`);
    const avatarStore = useAvatarStore();
    const saveImage = async () => {
      if (file.value == null) return;

      if (mimeType.value === 'image/gif') {
        if (props.excludeGif) return;

        axios
          .post(`${avatarUploadURL.value}/upload/${props.avatarId}`, file.value, {
            headers: {
              Authorization: `Bearer ${token.value}`,
            },
          })
          .then(() => {
            avatarStore.setAvatar(props.avatarId);
            addNotification({
              message: 'The avatar has been updated.',
              type: 'success',
            });

            emit('avatar-updated');
          })
          .catch((error) => {
            addNotification({
              message: error.message,
              type: 'danger',
            });
          })
          .finally(() => {
            fileInput.value.cleanupFileInput();

            selectedFile.value = '';
            mimeType.value = '';
          });
      } else if (cropperSelectionEl.value != null) {
        const canvas = await cropperSelectionEl.value.$toCanvas({
          width: props.cropperCanvasSize,
          height: props.cropperCanvasSize,
        });
        croppedImage.value = canvas.toDataURL();
        canvas.toBlob((blob) => {
          axios
            .post(`${avatarUploadURL.value}/upload/${props.avatarId}`, blob, {
              headers: {
                Authorization: `Bearer ${token.value}`,
              },
            })
            .then(() => {
              avatarStore.setAvatar(props.avatarId);
              addNotification({
                message: 'The avatar has been updated.',
                type: 'success',
              });

              emit('avatar-updated');
            })
            .catch((error) => {
              addNotification({
                message: error.message,
                type: 'danger',
              });
            })
            .finally(() => {
              fileInput.value.cleanupFileInput();

              selectedFile.value = '';
              croppedImage.value = '';
              mimeType.value = '';
            });
        }, mimeType.value);
      }
    };

    const reset = () => {
      fileInput.value.cleanupFileInput();

      selectedFile.value = '';
      croppedImage.value = '';
      mimeType.value = '';
    };

    return {
      token,
      currentAvatar,

      fileInput,
      onFileSelect,
      selectedFile,
      selectedFileString,

      cropperCanvasEl,
      cropperImageEl,
      cropperSelectionEl,
      croppedImage,

      mimeType,
      saveImage,

      reset,
    };
  },
});
</script>
